import React from 'react';
import { Server } from '../../server/server';
import { getBannerImage, getPortraitImage, msOfNow, uniqueId } from '../util/util';
import { BsCalendarWeek, BsChatDotsFill, BsGiftFill, BsFillEyeFill, BsFillEyeSlashFill, BsFillPersonPlusFill, BsPencilFill } from 'react-icons/bs';
import { publish, subscribe } from '../util/event';
import MessageModal from '../modals/MessageModal';
import AlertModal from '../modals/AlertModal';
import { Navigate } from 'react-router-dom';
import GiftAssetModal from '../modals/GiftAssetModal';
import QuestionModal from '../modals/QuestionModal';
import UserListModal from '../modals/UserListModal';
import EditProfileModal from '../modals/EditProfileModal';
import './ProfilePage.css'

interface ProfilePageState {
  profile: any;
  followers: any[];
  following: any[];
  message: string;
  alert: string;
  question: string;
  navigate: string;
  openEditProfile: boolean;
  openGiftModal: boolean;
  openUserList: boolean;
  userListTitle: string;
  userList: string[]
}

class ProfilePage extends React.Component<{}, ProfilePageState> {
  constructor(props: {}) {
    super(props);

    this.state = {
      profile: null,
      followers: [],
      following: [],
      message: '',
      alert: '',
      question: '',
      navigate: '',
      openEditProfile: false,
      openGiftModal: false,
      openUserList: false,
      userListTitle: 'Following',
      userList: []
    };

    this.onPopState = this.onPopState.bind(this);
    this.onFriendUpdated = this.onFriendUpdated.bind(this);
    this.onSendMessage = this.onSendMessage.bind(this);
    this.openGiftModal = this.openGiftModal.bind(this);
    this.onCloseGiftModal = this.onCloseGiftModal.bind(this);
    this.onQuestionYes = this.onQuestionYes.bind(this);
    this.onQuestionNo = this.onQuestionNo.bind(this);
    this.openUserList = this.openUserList.bind(this);
    this.onCloseUserList = this.onCloseUserList.bind(this);
    this.openEditProfile = this.openEditProfile.bind(this);
    this.onCloseEditProfile = this.onCloseEditProfile.bind(this);

    subscribe('show-my-profile', () => {
      this.loadProfile(Server.user.getSlug());
    });

    subscribe('on-flow', () => {
      this.loadProfile();
    });
  }

  componentDidMount() {
    this.loadProfile();
    window.addEventListener('popstate', this.onPopState);
    Server.user.addEventListener('friend-updated', this.onFriendUpdated);
  }

  componentWillUnmount() {
    window.removeEventListener('popstate', this.onPopState);
    Server.user.removeEventListener('friend-updated', this.onFriendUpdated);
  }

  onPopState(event:any) {
    this.loadProfile();
  }

  onFriendUpdated(event:any) {
    this.forceUpdate();
  }

  async loadProfile(slug:string = '') {
    if(slug == '')
      slug = window.location.pathname.substring(9);

    let response = await Server.public.loadProfileFromSlug(slug);
    if(!response.success) 
      return;

    this.setState({profile: response.profile});

    this.getFollowersAndFollowing(response.profile);
  }

  async getFollowersAndFollowing(profile:any) {
    let isCurrent = false, isFriend = false;

    if (Server.account.isLoggedIn()) {
      isCurrent = Server.user.isCurrent(profile.id);
      isFriend  = Server.user.isFriend(profile.id);
    }

    if(!isCurrent && !isFriend) {
      this.setState({followers: [], following: []});
      return;
    }

    let userFollowing = [];
    let userFollowers = [];
    
    if(isCurrent) {
      userFollowing = Server.user.getFollowing();
      userFollowers = Server.user.getFollowers();
    }
    else {
      let response = await Server.user.getFriendFollows(profile.id);
      if(!response.success) {
        this.setState({followers: [], following: []});
        return;
      }
      userFollowing = response.following;
      userFollowers = response.followers;
    }

    this.setState({
      followers: userFollowers,
      following: userFollowing
    });
  }

  onSendMessage() {
    let chat = Server.messages.getDirectChat(this.state.profile.id);

    if(chat) {
      this.setState({navigate: '/chat/' + chat.id});
      return;
    }

    this.setState({message: 'Starting chat...'});

    setTimeout(async () => {
      let response = await Server.messages.startDirectChat(this.state.profile.id);

      if(response.success) {
        chat = Server.messages.getDirectChat(this.state.profile.id);
        this.setState({navigate: '/chat/' + chat.id});
      }
      else {
        this.setState({alert: response.message, message: ''})
      }
    }, 500);
  }

  openGiftModal() {
    this.setState({openGiftModal: true});
  }

  onCloseGiftModal() {
    this.setState({openGiftModal: false});
  }

  async onFollow() {
    let isFollowing = Server.user.isFollowing(this.state.profile.id);
    let response:any = null;
    if (isFollowing) {
      this.setState({ message: 'Unfollowing...' });
      response = await Server.user.unfollow(this.state.profile.id);
    }
    else {
      this.setState({ message: 'Following...' });
      response = await Server.user.follow(this.state.profile.id);
    }

    setTimeout(() => {
    
      if (response.success) {
        this.setState({message: ''});
        publish('on-flow');
      }
      else 
        this.setState({message: ''});
    }, 500);
  }

  async onInvite() {
    let friend = Server.user.getFriend(this.state.profile.id);
    if(!friend)
      this.setState({question: `Invite ${this.state.profile.name} to be your friend?`});
    else
      this.setState({alert: `You have already sent ${this.state.profile.name} an invite.`})
  }

  onQuestionYes() {
    this.setState({question: '', message: 'Sending invite...'});
    setTimeout(async () => {
      await Server.user.inviteFriendBySlug(this.state.profile.slug);
      this.setState({message: '', alert: `Invite sent!`});
    }, 500);
  }

  onQuestionNo() {
    this.setState({question: ''});
  }

  async openUserList(mode:string) {
    let userIds = [];
    
    let follows = (mode == 'following') ? this.state.following : this.state.followers;
    for(let i = 0; i < follows.length; i++)
      userIds.push(follows[i]);

    if(userIds.length == 0)
      return;

    if(mode == 'following')
      this.setState({openUserList: true, userListTitle: 'Following', userList: []});
    else
      this.setState({openUserList: true, userListTitle: 'Followers', userList: []});

    await Server.public.loadProfiles(userIds);

    this.setState({userList: userIds});
  }

  onCloseUserList(slug:string) {
    this.setState({openUserList: false});
    if(slug) 
      this.loadProfile(slug);
  }

  openEditProfile() {
    this.setState({openEditProfile: true});
  }

  onCloseEditProfile(slug:string) {
    this.setState({openEditProfile: false});
    if(slug) 
      window.location.assign('/profile/' + slug);
    else
      this.loadProfile();
  }

  renderActionButtons() {
    if(!Server.account.isLoggedIn())
      return (<div className="profile-page-button-container" style={{height: '42px'}}></div>);

    if(this.state.profile.slug == Server.user.getSlug()) {
      return (
        <div className="profile-page-button-container">
          <div onClick={this.openEditProfile} className="profile-page-action-button">
            <BsPencilFill />
          </div>
        </div>      
      )
    }

    let isFriend = Server.user.isFriend(this.state.profile.id);
    let isFollowing = Server.user.isFollowing(this.state.profile.id);

    return (
      <div className="profile-page-button-container">
        {isFriend &&
          <div className="profile-page-action-button" onClick={this.onSendMessage}><BsChatDotsFill/></div>
        }

        {isFriend &&
          <div className="profile-page-action-button" onClick={()=>this.openGiftModal()}><BsGiftFill/></div>
        }

        {!isFriend && 
          <div className="profile-page-action-button" onClick={() => this.onInvite()}><BsFillPersonPlusFill/></div>
        }

        {!isFriend && !isFollowing && 
          <div className="profile-page-action-button" onClick={() => this.onFollow()}><BsFillEyeFill/></div>
        }

        {!isFriend && isFollowing && 
          <div className="profile-page-action-button" onClick={() => this.onFollow()}><BsFillEyeSlashFill/></div>
        }
      </div>
    )
  }

  render() {
    if(this.state.navigate != '') 
      return <Navigate to={this.state.navigate} />;

    if (this.state.profile == null) {
      return (
        <div className='profile-page'>
          <div className='profile-page-loading'>Loading...</div>
        </div>
      )
    }
      
    let joined = new Date(this.state.profile.joined).toLocaleDateString();

    let isFriend = false;
    let isCurrent = false;
    let friendOnline = false;

    if (Server.account.isLoggedIn()) {
      isFriend    = Server.user.isFriend(this.state.profile.id);
      isCurrent   = Server.user.isCurrent(this.state.profile.id);
      let friend = Server.user.getFriend(this.state.profile.id);
      friendOnline = friend && friend.presence && friend.presence.online;
    }
    
    let bannerImage = getBannerImage(this.state.profile);
    let portraitImage = getPortraitImage(this.state.profile);

    return (
      <div className='profile-page'>
        <div className='profile-page-container'>
          <div className='profile-page-header'>
            <img className="profile-page-banner" src={bannerImage} />
            <img className="profile-page-portrait" src={portraitImage} />

            {friendOnline &&
              <div className="profile-page-user-online" />
            }
          </div>

          {this.renderActionButtons()}

          <div className="profile-page-name">{this.state.profile.name}</div>
          <div className="profile-page-slug">@{this.state.profile.slug}</div>
          <div className="profile-page-desc">{this.state.profile.bio}</div>
          <div className='profile-page-joined-container'>
            <BsCalendarWeek color='white'/>
            <div className='profile-page-joined'>Joined {joined}</div>
          </div>

          { (isFriend || isCurrent) && 
            <div className='profile-page-follow-container'>
              <div className="profile-page-follow-link" onClick={() => this.openUserList('following')}>
                {this.state.following.length} Following
              </div>
              <div className="profile-page-follow-link" onClick={() => this.openUserList('followers')}>
                {this.state.followers.length} Followers
              </div>
            </div>
          }
        </div>

        <EditProfileModal open={this.state.openEditProfile} onClose={this.onCloseEditProfile} />
        <GiftAssetModal open={this.state.openGiftModal} friend={this.state.profile.id} onClose={this.onCloseGiftModal} />
        <UserListModal open={this.state.openUserList} title={this.state.userListTitle} users={this.state.userList} onClose={this.onCloseUserList} profile={true} />
        <MessageModal message={this.state.message} />
        <AlertModal message={this.state.alert} button="OK" onClose={()=>this.setState({alert: ''})}/>
        <QuestionModal message={this.state.question} onYes={this.onQuestionYes} onNo={this.onQuestionNo} />
      </div>
    );
  }
}

export default ProfilePage;